import React, { useState, useEffect, useMemo, useRef } from 'react';
import { LayoutGrid, Plus, User, Loader2, X, ChevronDown, Check, ArrowRightLeft, Trash2, RefreshCcw, Palette, ShieldCheck, Download, Share2, Camera, Settings2, Users, Grid3X3 } from 'lucide-react';
import { Game, Language } from '../../types';
import { fetchRecentLineupPlayers } from '../../services/footballService';
import { formatLocalDateTime } from '../../services/formatService';
import { uploadSharedImage } from '../../services/authService';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { getOracleConfig } from '../../services/authService';
import { getTeamName } from '../../services/teamService';
import { ConfirmModal } from '../Common/ConfirmModal';
import { t } from '../../i18n';
import html2canvas from 'html2canvas';

interface SquadBuilderProps {
  game: Game;
  lang: Language;
  season: string;
  onShareToComments?: (imageUrl: string) => void;
}

type Formation = '4-3-3' | '4-4-2' | '3-4-3' | '5-3-2' | '4-2-3-1';
type SquadTheme = 'Grass' | 'Stadium' | 'Blueprint' | 'Stealth' | 'Premium' | 'Glass' | 'Paper' | 'Cyber' | 'Classic' | 'RetroMud' | 'NeonFuture';
type FrameType = 'none' | 'gold' | 'diamond' | 'line' | 'red';

const PLAYER_FRAMES: { id: FrameType; label: string; image?: string }[] = [
    { id: 'none', label: 'frameNone', image: '' },
    { id: 'gold', label: 'frameGold', image: '../assets/frames/golden.png' },
    { id: 'diamond', label: 'frameStone', image: '../assets/frames/diamond.png' },
    { id: 'line', label: 'frameLine', image: '../assets/frames/line.png' },
    { id: 'red', label: 'frameRed', image: '../assets/frames/red.png' },
];

const mapPosToFilter = (pos: string): 'Forwards' | 'Midfielders' | 'Defenders' | 'Goalkeepers' | 'All' => {
  const p = pos.toUpperCase();
  if (p === 'GK') return 'Goalkeepers';
  if (['CB', 'LB', 'RB', 'LWB', 'RWB', 'D', 'DEF'].includes(p)) return 'Defenders';
  if (['CM', 'LM', 'RM', 'CDM', 'CAM', 'M', 'MID', 'LDM', 'RDM'].includes(p)) return 'Midfielders';
  if (['ST', 'LW', 'RW', 'CF', 'A', 'ATT', 'FW', 'LAM', 'RAM'].includes(p)) return 'Forwards';
  return 'All';
};

const FORMATIONS: Record<Formation, { pos: string; x: number; y: number }[]> = {
  '4-3-3': [
    { pos: 'GK', x: 50, y: 94 },
    { pos: 'LB', x: 15, y: 77 }, { pos: 'CB', x: 38, y: 79 }, { pos: 'CB', x: 62, y: 79 }, { pos: 'RB', x: 85, y: 77 },
    { pos: 'CM', x: 26, y: 57 }, { pos: 'CM', x: 50, y: 59 }, { pos: 'CM', x: 74, y: 57 },
    { pos: 'LW', x: 22, y: 39 }, { pos: 'ST', x: 50, y: 35 }, { pos: 'RW', x: 78, y: 39 }
  ],
  '4-4-2': [
    { pos: 'GK', x: 50, y: 94 },
    { pos: 'LB', x: 15, y: 77 }, { pos: 'CB', x: 38, y: 79 }, { pos: 'CB', x: 62, y: 79 }, { pos: 'RB', x: 85, y: 77 },
    { pos: 'LM', x: 18, y: 57 }, { pos: 'CM', x: 40, y: 59 }, { pos: 'CM', x: 60, y: 59 }, { pos: 'RM', x: 82, y: 57 },
    { pos: 'ST', x: 35, y: 41 }, { pos: 'ST', x: 65, y: 41 }
  ],
  '3-4-3': [
    { pos: 'GK', x: 50, y: 94 },
    { pos: 'CB', x: 26, y: 75 }, { pos: 'CB', x: 50, y: 78 }, { pos: 'CB', x: 74, y: 75 },
    { pos: 'LM', x: 18, y: 57 }, { pos: 'CM', x: 40, y: 59 }, { pos: 'CM', x: 60, y: 59 }, { pos: 'RM', x: 82, y: 57 },
    { pos: 'LW', x: 22, y: 40 }, { pos: 'ST', x: 50, y: 36 }, { pos: 'RW', x: 78, y: 40 }
  ],
  '5-3-2': [
    { pos: 'GK', x: 50, y: 94 },
    { pos: 'LWB', x: 15, y: 70 }, { pos: 'CB', x: 32, y: 75 }, { pos: 'CB', x: 50, y: 77 }, { pos: 'CB', x: 68, y: 75 }, { pos: 'RWB', x: 85, y: 70 },
    { pos: 'CM', x: 26, y: 53 }, { pos: 'CM', x: 50, y: 55 }, { pos: 'CM', x: 74, y: 53 },
    { pos: 'ST', x: 35, y: 37 }, { pos: 'ST', x: 65, y: 37 }
  ],
  '4-2-3-1': [
    { pos: 'GK', x: 50, y: 94 },
    { pos: 'LB', x: 15, y: 77 }, { pos: 'CB', x: 38, y: 79 }, { pos: 'CB', x: 62, y: 79 }, { pos: 'RB', x: 85, y: 77 },
    { pos: 'LDM', x: 38, y: 62 }, { pos: 'RDM', x: 62, y: 62 },
    { pos: 'LAM', x: 22, y: 47 }, { pos: 'CAM', x: 50, y: 49 }, { pos: 'RAM', x: 78, y: 47 },
    { pos: 'ST', x: 50, y: 34 }
  ]
};

const FORMATION_GUIDE_TEXT = {
    ko: {
      capturing: t('ko', 'capturingImage'),
      title: t('ko', 'squadBuilderTitle'),
    subtitle: 'SQUAD BUILDER',
    formation: t('ko', 'formation'),
    theme: t('ko', 'theme'),
    save: t('ko', 'saveImage'),
    share: t('ko', 'shareToFeed'),
    swapTip: t('ko', 'swapTip'),
    captureTitle: t('ko', 'squadAnalysis'),
    captureSub: 'XLEVEN SQUAD BUILDER',
  },
  en: {
    capturing: t('en', 'capturingImage'),
    title: t('en', 'squadBuilderTitle'),
    subtitle: 'SQUAD BUILDER',
    formation: t('en', 'formation'),
    theme: t('en', 'theme'),
    save: t('en', 'saveImage'),
    share: t('en', 'shareToFeed'),
    swapTip: t('en', 'swapTip'),
    captureTitle: t('en', 'squadAnalysis'),
    captureSub: 'XLEVEN SQUAD BUILDER',
  },
};

const SPONSORS = [
    { name: 'XLEVEN', color: 'text-rose-500' },
    { name: 'YouTube', color: 'text-white' },
    { name: 'BBC Sport', color: 'text-white' },
    { name: 'Pexels', color: 'text-white' },
    { name: 'API-Sports', color: 'text-white' },
    // { name: 'XLEVEN SPORTS', color: 'text-rose-400' },
];

const THEME_CONFIG: Record<SquadTheme, { 
    bg: string; 
    lineColor: string; 
    pattern?: string;
    neonGlow?: boolean;
    centerLineBg: string;
    overlay?: string;
}> = {
    'Grass': { 
        bg: 'bg-[#2d5a27]', 
        lineColor: 'border-white/60', 
        centerLineBg: 'bg-white/40',
        pattern: 'data:image/svg+xml,%3Csvg width="100" height="160" viewBox="0 0 100 160" xmlns="http://www.w3.org/2000/svg"%3E%3Crect width="100" height="80" fill="%23356a2e"/%3E%3Crect y="80" width="100" height="80" fill="%232d5a27"/%3E%3C/svg%3E'
    },
    'Stadium': {
        bg: 'bg-[#1b4318]',
        lineColor: 'border-white/80',
        centerLineBg: 'bg-white/50',
        pattern: 'data:image/svg+xml,%3Csvg width="160" height="160" viewBox="0 0 160 160" xmlns="http://www.w3.org/2000/svg"%3E%3Crect width="80" height="160" fill="%2322521d"/%3E%3Crect x="80" width="80" height="160" fill="%231b4318"/%3E%3Cfilter id="n"%3E%3CfeTurbulence type="fractalNoise" baseFrequency="0.7" numOctaves="3" stitchTiles="stitch"/%3E%3C/filter%3E%3Crect width="160" height="160" filter="url(%23n)" opacity="0.15"/%3E%3C/svg%3E',
        overlay: 'radial-gradient(circle at center, rgba(255,255,255,0.15) 0%, rgba(0,0,0,0.4) 100%)'
    },
    'Blueprint': { 
        bg: 'bg-[#002855]', 
        lineColor: 'border-blue-400/40', 
        centerLineBg: 'bg-blue-400/20',
        pattern: 'data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%2360a5fa" fill-opacity="0.1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E'
    },
    'Stealth': { 
        bg: 'bg-slate-950', 
        lineColor: 'border-rose-500/30', 
        centerLineBg: 'bg-rose-500/10',
        pattern: 'data:image/svg+xml,%3Csvg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="%23ffffff" fill-opacity="0.02" fill-rule="evenodd"%3E%3Ccircle cx="3" cy="3" r="1"/%3E%3Ccircle cx="13" cy="13" r="1"/%3E%3C/g%3E%3C/svg%3E'
    },
    'Premium': { 
        bg: 'bg-[#1a1a1a]', 
        lineColor: 'border-amber-500/30', 
        centerLineBg: 'bg-amber-500/10',
        pattern: 'data:image/svg+xml,%3Csvg width="4" height="4" viewBox="0 0 4 4" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M1 3h1v1H1V3zm2-2h1v1H3V1z" fill="%23d97706" fill-opacity="0.1" fill-rule="evenodd"/%3E%3C/svg%3E'
    },
    'Glass': { 
        bg: 'bg-indigo-900', 
        lineColor: 'border-white/20', 
        centerLineBg: 'bg-white/10',
        pattern: 'data:image/svg+xml,%3Csvg width="52" height="26" viewBox="0 0 52 26" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Cpath d="M10 10c0-2.21-1.79-4-4-4-3.314 0-6-2.686-6-6h2c0 2.21 1.79 4 4 4 3.314 0 6 2.686 6 6 0 2.21 1.79 4 4 4h2c-2.21 0-4-1.79-4-4zm25.464-1.95l8.486 8.486-1.414 1.414-8.486-8.486 1.414-1.414z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E'
    },
    'Paper': { 
        bg: 'bg-[#f1f0e8]', 
        lineColor: 'border-slate-900/10', 
        centerLineBg: 'bg-slate-900/5',
        pattern: 'data:image/svg+xml,%3Csvg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"%3E%3Cfilter id="noise"%3E%3CfeTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="4" stitchTiles="stitch"/%3E%3C/filter%3E%3Crect width="100" height="100" filter="url(%23noise)" opacity="0.05"/%3E%3C/svg%3E'
    },
    'Cyber': { 
        bg: 'bg-[#050510]', 
        lineColor: 'border-cyan-500/40', 
        centerLineBg: 'bg-cyan-500/20',
        pattern: 'data:image/svg+xml,%3Csvg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M0 40L40 0H20L0 20M40 40V20L20 40" fill="%2306b6d4" fill-opacity="0.05"/%3E%3C/svg%3E',
        neonGlow: true
    },
    'Classic': {
        bg: 'bg-[#4c9900]',
        lineColor: 'border-white/70',
        centerLineBg: 'bg-white/40',
        pattern: 'data:image/svg+xml,%3Csvg width="100" height="160" viewBox="0 0 100 160" xmlns="http://www.w3.org/2000/svg"%3E%3Crect width="100" height="20" fill="%2355aa00"/%3E%3Crect y="20" width="100" height="20" fill="%234c9900"/%3E%3Crect y="40" width="100" height="20" fill="%2355aa00"/%3E%3Crect y="60" width="100" height="20" fill="%234c9900"/%3E%3Crect y="80" width="100" height="20" fill="%2355aa00"/%3E%3Crect y="100" width="100" height="20" fill="%234c9900"/%3E%3Crect y="120" width="100" height="20" fill="%2355aa00"/%3E%3Crect y="140" width="100" height="20" fill="%234c9900"/%3E%3C/svg%3E',
        overlay: 'linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0) 20%, rgba(0,0,0,0) 80%, rgba(0,0,0,0.2) 100%)'
    },
    'RetroMud': {
        bg: 'bg-[#5d4037]',
        lineColor: 'border-white/30',
        centerLineBg: 'bg-white/10',
        pattern: 'data:image/svg+xml,%3Csvg width="200" height="200" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"%3E%3Cfilter id="noise"%3E%3CfeTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" stitchTiles="stitch"/%3E%3C/filter%3E%3Crect width="200" height="200" filter="url(%23noise)" opacity="0.4"/%3E%3C/svg%3E',
        overlay: 'radial-gradient(circle at center, rgba(0,0,0,0) 0%, rgba(0,0,0,0.6) 100%)'
    },
    'NeonFuture': {
        bg: 'bg-[#1a0b2e]',
        lineColor: 'border-fuchsia-500/50',
        centerLineBg: 'bg-fuchsia-500/20',
        pattern: 'data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M0 0h60v60H0z" fill="none"/%3E%3Cpath d="M30 0v60M0 30h60" stroke="%23d946ef" stroke-opacity="0.1" stroke-width="1"/%3E%3C/svg%3E',
        neonGlow: true,
        overlay: 'linear-gradient(45deg, rgba(217,70,239,0.1) 0%, rgba(0,0,0,0) 50%, rgba(14,165,233,0.1) 100%)'
    }
};

export const SquadBuilder: React.FC<SquadBuilderProps> = ({ game, lang, season, onShareToComments }) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [showCaptureLoader, setShowCaptureLoader] = useState(false);
  const isCaptureUrlMode = window.location.search.includes('capture=true') || window.location.hash.includes('capture=true');

  const urlParams = new URLSearchParams(window.location.search);
  const paramLineup = urlParams.get('captureLineup');
  const paramTheme = urlParams.get('captureTheme');
  const paramFormation = urlParams.get('captureFormation');
  const paramTeamId = urlParams.get('captureTeamId');

  const [activeTeamId, setActiveTeamId] = useState(paramTeamId || game.homeTeam.id || '');
  const [formation, setFormation] = useState<Formation>((paramFormation as Formation) || '4-3-3');
  const [currentTheme, setCurrentTheme] = useState<SquadTheme>((paramTheme as SquadTheme) || 'Classic');
  const [lineup, setLineup] = useState<Record<number, any>>(paramLineup ? JSON.parse(paramLineup) : {});
  
  // Interaction States
  const [rotation, setRotation] = useState({ x: 35, y: 0 });
  const [isInteracting, setIsInteracting] = useState(false);
  const lastPos = useRef({ x: 0, y: 0 });

  // Modals visibility states
  const [isPlayerModalOpen, setIsPlayerModalOpen] = useState(false);
  const [isFormationModalOpen, setIsFormationModalOpen] = useState(false);
  const [isThemeModalOpen, setIsThemeModalOpen] = useState(false);
  const [isFrameModalOpen, setIsFrameModalOpen] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  
  // Animation states for modals
  const [isPlayerAnimating, setIsPlayerAnimating] = useState(false);
  const [isFormationAnimating, setIsFormationAnimating] = useState(false);
  const [isThemeAnimating, setIsThemeAnimating] = useState(false);
  const [isFrameAnimating, setIsFrameAnimating] = useState(false);

  const [selectingIndex, setSelectingIndex] = useState<number | null>(null);
  const [players, setPlayers] = useState<any[]>([]);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [activeFilter, setActiveFilter] = useState<'Forwards' | 'Midfielders' | 'Defenders' | 'Goalkeepers' | 'All'>('Forwards');
  
  const [activeSlotMenu, setActiveSlotMenu] = useState<number | null>(null);
  const [isMovingPlayer, setIsMovingPlayer] = useState<number | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const fieldRef = useRef<HTMLDivElement>(null);
  const slotMenuRef = useRef<HTMLDivElement>(null);

  const currentTeamName = getTeamName(activeTeamId, lang, activeTeamId === game.homeTeam.id ? game.homeTeam.name : game.awayTeam.name);
  const currentTeamLogo = activeTeamId === game.homeTeam.id ? game.homeTeam.logo : game.awayTeam.logo;

  const selectedPlayerIds = useMemo(() => {
    return Object.values(lineup).filter((p: any) => p && p.player).map((p: any) => p.player.id);
  }, [lineup]);

  const currentThemeConfig = THEME_CONFIG[currentTheme];

  const formattedDate = useMemo(() => {
    const fixtureDate = (game as any).fixture?.date || game.dateLabel;
    if (!fixtureDate) return '';
    try {
      const date = new Date(fixtureDate);
      const locale = lang === 'ko' ? 'ko-KR' : 'en-US';
      const options: Intl.DateTimeFormatOptions = {
        weekday: 'long',
        month: 'long',
        day: 'numeric'
      };
      if (lang === 'ko') {
        // ensure KO uses Seoul timezone when available
        (options as any).timeZone = 'Asia/Seoul';
      }
      return new Intl.DateTimeFormat(locale, options).format(date);
    } catch (e) {
      return fixtureDate || '';
    }
  }, [game, lang]);

  // Auto-reset rotation
  useEffect(() => {
    if (!isInteracting) {
        const timer = setTimeout(() => {
            setRotation({ x: 35, y: 0 });
        }, 3000);
        return () => clearTimeout(timer);
    }
  }, [isInteracting]);

  const handleInteractionStart = (e: React.MouseEvent | React.TouchEvent) => {
    if (activeSlotMenu !== null || selectingIndex !== null) return;
    
    // Prevent scrolling when interacting with the field
    if (e.cancelable) {
      e.preventDefault();
    }
    
    setIsInteracting(true);
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    lastPos.current = { x: clientX, y: clientY };
  };

  const handleInteractionMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isInteracting) return;
    
    // Prevent scrolling and other gestures
    if (e.cancelable) {
      e.preventDefault();
    }

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    const deltaX = clientX - lastPos.current.x;
    const deltaY = clientY - lastPos.current.y;

    setRotation(prev => ({
        x: Math.max(15, Math.min(60, prev.x - deltaY * 0.2)),
        y: Math.max(-25, Math.min(25, prev.y + deltaX * 0.2))
    }));

    lastPos.current = { x: clientX, y: clientY };
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (slotMenuRef.current && !slotMenuRef.current.contains(e.target as Node)) {
        setActiveSlotMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (activeTeamId) {
      setLoadingPlayers(true);
      const loadTeamData = async () => {
        try {
          const recentMatchPlayers = await fetchRecentLineupPlayers(activeTeamId);
          setPlayers(recentMatchPlayers);
        } catch (e) {
          console.error("Squad loading failed", e);
        } finally {
          setLoadingPlayers(false);
        }
      };
      loadTeamData();
    }
  }, [activeTeamId]);

  // Modal Animation Triggers
  useEffect(() => {
    if (isPlayerModalOpen) setTimeout(() => setIsPlayerAnimating(true), 10);
    else setIsPlayerAnimating(false);
  }, [isPlayerModalOpen]);

  useEffect(() => {
    if (isFormationModalOpen) setTimeout(() => setIsFormationAnimating(true), 10);
    else setIsFormationAnimating(false);
  }, [isFormationModalOpen]);

  useEffect(() => {
    if (isThemeModalOpen) setTimeout(() => setIsThemeAnimating(true), 10);
    else setIsThemeAnimating(false);
  }, [isThemeModalOpen]);

  useEffect(() => {
    if (isFrameModalOpen) setTimeout(() => setIsFrameAnimating(true), 10);
    else setIsFrameAnimating(false);
  }, [isFrameModalOpen]);

  const playerCounts = useMemo(() => {
    const counts = { All: players.length, Forwards: 0, Midfielders: 0, Defenders: 0, Goalkeepers: 0 };
    players.forEach(p => {
      const pos = p.statistics?.[0]?.games?.position || '';
      if (pos === 'Attacker') counts.Forwards++;
      else if (pos === 'Midfielder') counts.Midfielders++;
      else if (pos === 'Defender') counts.Defenders++;
      else if (pos === 'Goalkeeper') counts.Goalkeepers++;
    });
    return counts;
  }, [players]);

  const filteredPlayers = useMemo(() => {
    const list = activeFilter === 'All' ? players : players.filter(p => {
        const statsPos = p.statistics?.[0]?.games?.position || '';
        if (activeFilter === 'Forwards') return statsPos === 'Attacker';
        if (activeFilter === 'Midfielders') return statsPos === 'Midfielder';
        if (activeFilter === 'Defenders') return statsPos === 'Defender';
        if (activeFilter === 'Goalkeepers') return statsPos === 'Goalkeeper';
        return true;
    });

    return list.sort((a, b) => {
        const rA = parseFloat(a.statistics?.[0]?.games?.rating || '0');
        const rB = parseFloat(b.statistics?.[0]?.games?.rating || '0');
        return rB - rA;
    });
  }, [players, activeFilter]);

  const openPlayerSelect = (index: number) => {
    const slotPos = FORMATIONS[formation][index].pos;
    const recommendedFilter = mapPosToFilter(slotPos);
    setActiveFilter(recommendedFilter);
    setSelectingIndex(index);
    setIsPlayerModalOpen(true);
  };

  const handleClosePlayerModal = () => {
    setIsPlayerAnimating(false);
    setTimeout(() => {
      setIsPlayerModalOpen(false);
      setSelectingIndex(null);
    }, 500);
  };

  const handleCloseFormationModal = () => {
    setIsFormationAnimating(false);
    setTimeout(() => setIsFormationModalOpen(false), 500);
  };

  const handleCloseThemeModal = () => {
    setIsThemeAnimating(false);
    setTimeout(() => setIsThemeModalOpen(false), 500);
  };

  const handleCloseFrameModal = () => {
    setIsFrameAnimating(false);
    setTimeout(() => setIsFrameAnimating(false), 500);
  };

  const selectPlayer = (player: any) => {
    if (selectedPlayerIds.includes(player.player.id)) return;
    if (selectingIndex !== null) {
      setLineup(prev => ({ 
        ...prev, 
        [selectingIndex]: { ...player, frame: 'none' } 
      }));
    }
    handleClosePlayerModal();
  };

  const selectFrame = (frameId: FrameType) => {
    if (selectingIndex !== null) {
      setLineup(prev => ({
        ...prev,
        [selectingIndex]: { ...prev[selectingIndex], frame: frameId }
      }));
    }
    handleCloseFrameModal();
  };

  const swapPlayers = (sourceIdx: number, targetIdx: number) => {
    if (sourceIdx === targetIdx) return;
    const sourceData = lineup[sourceIdx];
    const targetData = lineup[targetIdx];
    
    setLineup(prev => ({
      ...prev,
      [targetIdx]: sourceData,
      [sourceIdx]: targetData
    }));
  };

  const handleSlotClick = (idx: number) => {
    if (isMovingPlayer !== null) {
      swapPlayers(isMovingPlayer, idx);
      setIsMovingPlayer(null);
      return;
    }

    if (lineup[idx]) {
      setActiveSlotMenu(activeSlotMenu === idx ? null : idx);
    } else {
      openPlayerSelect(idx);
    }
  };

  const handleCapture = async (action: 'download' | 'share') => {
    if (!user) {
      const currentPath = window.getCurrentPath ? window.getCurrentPath() : window.location.pathname;
      sessionStorage.setItem('redirectAfterLogin', currentPath);
      setLoginModalOpen(true);
      return;
    }

    if (!fieldRef.current || isCapturing) return;

    setActiveSlotMenu(null);
    setIsCapturing(true);
    setShowCaptureLoader(true);

    try {
      const { host, adminKey } = getOracleConfig();
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('captureLineup', JSON.stringify(lineup));
      currentUrl.searchParams.set('captureTheme', currentTheme);
      currentUrl.searchParams.set('captureFormation', formation);
      currentUrl.searchParams.set('captureTeamId', activeTeamId);
      currentUrl.searchParams.set('capture', 'true');
      
      const url = encodeURIComponent(currentUrl.toString());
      
      const response = await fetch(`${host}/api/capture?url=${url}&hideSelector=.capture-ignore,#guest-timer-banner&wait=2000`, {
        headers: {
          'x-api-key': adminKey
        }
      });

      if (!response.ok) throw new Error('Capture API failed');
      
      const blob = await response.blob();

      if (action === 'download') {
        const imgUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = `XLEVEN-Squad-${currentTeamName}.png`;
        link.href = imgUrl;
        link.click();
        URL.revokeObjectURL(imgUrl);
      } else if (action === 'share') {
        try {
          const imageUrl = await uploadSharedImage(user.id, blob);
          if (onShareToComments) {
            onShareToComments(imageUrl);
          }
        } catch (e) {
          showToast('error', t(lang, 'imageUploadFailed'), () => {
            setShowCaptureLoader(false);
          });
          setIsCapturing(false);
          return;
        }
      }
      setShowCaptureLoader(false);
    } catch (e) {
      console.error('Capture failed', e);
      showToast('error', t(lang, 'captureFailed'), () => {
        setShowCaptureLoader(false);
      });
    } finally {
      setIsCapturing(false);
    }
  };

  const onDragStart = (e: React.DragEvent, idx: number) => {
    if (!lineup[idx]) {
      e.preventDefault();
      return;
    }
    setDraggedIndex(idx);
    e.dataTransfer.setData('sourceIndex', idx.toString());
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== idx) {
      setDragOverIndex(idx);
    }
  };

  const onDrop = (e: React.DragEvent, targetIdx: number) => {
    e.preventDefault();
    const sourceIdxStr = e.dataTransfer.getData('sourceIndex');
    if (sourceIdxStr) {
      const sourceIdx = parseInt(sourceIdxStr);
      swapPlayers(sourceIdx, targetIdx);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const onDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const clearSlot = (idx: number) => {
    const newLineup = { ...lineup };
    delete newLineup[idx];
    setLineup(newLineup);
    setActiveSlotMenu(null);
  };

    const themeOptions: { id: SquadTheme; label: string; iconBg: string }[] = [
    { id: 'Classic', label: t(lang, 'classicGround'), iconBg: 'bg-[#4c9900]' },
    { id: 'Stadium', label: t(lang, 'realStadium'), iconBg: 'bg-[#1b4318]' },
    { id: 'Grass', label: t(lang, 'themeGrass'), iconBg: 'bg-[#2d5a27]' },
    { id: 'Blueprint', label: t(lang, 'themeBlueprint'), iconBg: 'bg-[#002855]' },
    { id: 'Stealth', label: t(lang, 'themeStealth'), iconBg: 'bg-slate-950' },
    { id: 'Premium', label: t(lang, 'themePremium'), iconBg: 'bg-amber-600' },
    { id: 'Paper', label: t(lang, 'themePaper'), iconBg: 'bg-[#f4f1ea]' },
    { id: 'Cyber', label: t(lang, 'themeCyber'), iconBg: 'bg-[#050510]' },
    { id: 'RetroMud', label: t(lang, 'themeRetroMud'), iconBg: 'bg-[#5d4037]' },
    { id: 'NeonFuture', label: t(lang, 'themeNeonFuture'), iconBg: 'bg-[#1a0b2e]' }
  ];

  const handleLoginRedirect = () => {
      const currentPath = window.getCurrentPath ? window.getCurrentPath() : window.location.pathname;
      sessionStorage.setItem('redirectAfterLogin', currentPath);
      window.navigateTo('/login');
  };

  const guide = FORMATION_GUIDE_TEXT[lang as keyof typeof FORMATION_GUIDE_TEXT] || FORMATION_GUIDE_TEXT.en;

  return (
    <div className="mb-12 space-y-8 animate-in fade-in duration-700">
      {showCaptureLoader && (
        <div className="fixed inset-0 z-[1000] flex flex-col items-center justify-center bg-black/80 backdrop-blur-md animate-in fade-in duration-300">
          <div className="relative w-32 h-32 mb-8">
            <div className="absolute inset-0 border-4 border-rose-500/20 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-rose-500 border-t-transparent rounded-full animate-spin"></div>
            <div className="absolute inset-4 bg-white/5 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/10">
              <Camera size={40} className="text-rose-500 animate-pulse" />
            </div>
            
            <div className="absolute -inset-4 border border-rose-500/10 rounded-full animate-ping opacity-20"></div>
          </div>
          <div className="flex flex-col items-center gap-2">
            <h3 className="text-white font-black text-xl uppercase tracking-tighter animate-pulse">
              {guide.capturing}
            </h3>
            <div className="flex gap-1.5">
              {[0, 1, 2].map((i) => (
                <div 
                  key={i} 
                  className="w-1.5 h-1.5 bg-rose-500 rounded-full animate-bounce"
                  style={{ animationDelay: `${i * 0.15}s` }}
                ></div>
              ))}
            </div>
          </div>
        </div>
      )}

      <ConfirmModal 
        isOpen={loginModalOpen} 
        onClose={() => setLoginModalOpen(false)} 
        onConfirm={handleLoginRedirect} 
        title={t(lang, 'loginRequiredTitle')} 
        confirmLabel={t(lang, 'signInLabel')}
        cancelLabel={t(lang, 'cancel')}
      />

      <div className="flex flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-slate-900 flex items-center justify-center text-white shadow-sm shrink-0">
             <Users size={18} />
          </div>
          <div>
            <h2 className="text-xs font-black text-slate-900 tracking-widest uppercase leading-none">{guide.title}</h2>
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mt-1">{guide.subtitle}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
            <button 
                onClick={() => setIsFormationModalOpen(true)}
                className="flex items-center gap-3 bg-slate-900 text-white px-5 py-3 rounded-2xl border border-white/10 shadow-md transition-all active:scale-95 group min-w-[110px] md:min-w-[140px] justify-between"
            >
                <span className="text-xs font-black uppercase tracking-[0.2em]">{formation}</span>
                <Settings2 size={16} className="text-rose-400" />
            </button>

            <button 
                onClick={() => setIsThemeModalOpen(true)}
                className="flex items-center justify-center w-12 h-12 bg-white text-slate-900 rounded-2xl border border-gray-100 shadow-md transition-all active:scale-95 group"
                title={guide.theme}
            >
                <Palette size={20} className="text-rose-500" />
            </button>
        </div>
      </div>

      {/* Team Toggle */}
      <div className="flex justify-center relative z-10">
        <div className="flex bg-slate-900 p-1.5 rounded-full border border-white/10 shadow-md">
          <button 
            onClick={() => { setActiveTeamId(game.homeTeam.id || ''); setLineup({}); }}
            className={`flex items-center gap-3 px-6 py-2.5 rounded-full transition-all ${activeTeamId === game.homeTeam.id ? 'bg-white text-slate-900 shadow-md' : 'text-white/50 md:hover:text-white'}`}
          >
            <img src={game.homeTeam.logo} className="w-5 h-5 object-contain" alt="" />
            <span className="text-[11px] font-black uppercase tracking-tight truncate max-w-[100px]">{getTeamName(game.homeTeam.id, lang, game.homeTeam.name)}</span>
          </button>
          <button 
            onClick={() => { setActiveTeamId(game.awayTeam.id || ''); setLineup({}); }}
            className={`flex items-center gap-3 px-6 py-2.5 rounded-full transition-all ${activeTeamId === game.awayTeam.id ? 'bg-white text-slate-900 shadow-md' : 'text-white/50 md:hover:text-white'}`}
          >
            <img src={game.awayTeam.logo} className="w-5 h-5 object-contain" alt="" />
            <span className="text-[11px] font-black uppercase tracking-tight truncate max-w-[100px]">{getTeamName(game.awayTeam.id, lang, game.awayTeam.name)}</span>
          </button>
        </div>
      </div>

      {/* Field Area */}
      <div 
        ref={fieldRef}
        id="capture-target" 
        className={`relative max-w-2xl mx-auto pb-10 select-none touch-none ${isCaptureUrlMode ? 'pt-2 bg-transparent' : '!-mt-20'}`}
      >
        {isCaptureUrlMode && (
          <div className="absolute top-6 left-0 right-0 h-28 flex flex-col items-center justify-center text-center z-[100] px-4 pointer-events-none bg-white">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] font-black text-rose-500 tracking-[0.3em] uppercase">XLEVEN</span>
            </div>
            
            <h1 className="text-3xl font-black text-slate-900 tracking-tighter mb-1 flex items-center gap-3">
              <span className="uppercase">{t(lang as Language, 'startingXI')}</span>
            </h1>

            <div className="flex items-center gap-4 mb-1.5">
              <span className="text-sm font-black text-slate-800 uppercase tracking-tight">{getTeamName(game.homeTeam.id, lang, game.homeTeam.name)}</span>
              <span className="text-[10px] font-black text-slate-400 italic">VS</span>
              <span className="text-sm font-black text-slate-800 uppercase tracking-tight">{getTeamName(game.awayTeam.id, lang, game.awayTeam.name)}</span>
            </div>

            <div className="flex items-center gap-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
              <span>{formattedDate}</span>
              {((game as any).fixture?.venue?.name || (game as any).venue) && (
                <>
                  <span className="w-1 h-1 rounded-full bg-slate-300" />
                  <span>{(game as any).fixture?.venue?.name || (game as any).venue}</span>
                </>
              )}
            </div>
          </div>
        )}

        <div className="absolute inset-0" style={{ perspective: '1200px' }}>
          <div 
            className={`w-full h-full rounded-[32px] shadow-2xl overflow-hidden transition-all duration-1000 ${currentThemeConfig.bg}`}
            style={{ 
                backgroundImage: currentThemeConfig.pattern ? `url(${currentThemeConfig.pattern})` : 'none',
                backgroundBlendMode: currentTheme === 'Paper' ? 'multiply' : (currentTheme === 'Stadium' || currentTheme === 'Grass') ? 'normal' : 'overlay',
                transform: `rotateX(${rotation.x}deg) rotateY(${rotation.y}deg)`,
                transformOrigin: 'center bottom',
                boxShadow: '0 50px 100px -20px rgba(0,0,0,0.5), inset 0 0 120px rgba(0,0,0,0.2)'
            }}
          >
            {currentThemeConfig.overlay && (
                <div className="absolute inset-0 pointer-events-none z-0 scale-[1.2]" style={{ background: currentThemeConfig.overlay }} />
            )}

            <div className={`absolute inset-0 ${currentThemeConfig.neonGlow ? 'opacity-80' : 'opacity-40'} pointer-events-none p-[6%]`}>
                <div className={`w-full h-full border-2 ${currentThemeConfig.lineColor} relative rounded-lg ${currentThemeConfig.neonGlow ? 'shadow-[0_0_15px_rgba(6,182,212,0.3)]' : ''}`}>
                    <div className={`absolute top-1/2 left-0 right-0 h-px ${currentTheme === 'Cyber' ? 'bg-cyan-500/50 shadow-[0_0_10px_rgba(6,182,212,0.5)]' : currentThemeConfig.centerLineBg} -translate-y-1/2`} />
                    <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1/3 aspect-square border-2 ${currentThemeConfig.lineColor} rounded-full ${currentThemeConfig.neonGlow ? 'shadow-[0_0_15px_rgba(6,182,212,0.3)]' : ''}`} />
                    <div className={`absolute top-0 left-1/4 right-1/4 h-[12%] border-2 border-t-0 ${currentThemeConfig.lineColor}`} />
                    <div className={`absolute bottom-0 left-1/4 right-1/4 h-[12%] border-2 border-b-0 ${currentThemeConfig.lineColor}`} />
                </div>
            </div>

            {/* Top Ad Boards */}
            <div className="absolute top-0 left-0 right-0 h-6 bg-black/80 flex items-center overflow-hidden border-b border-white/10 z-10">
                <div className="flex items-center gap-12 animate-marquee whitespace-nowrap px-4">
                    {[...SPONSORS, ...SPONSORS].map((s, i) => (
                        <div key={`top-1-${i}`} className="flex items-center gap-2">
                            <span className={`text-[9px] font-black italic tracking-tighter ${s.color}`}>{s.name}</span>
                        </div>
                    ))}
                </div>
                <div className="flex items-center gap-12 animate-marquee whitespace-nowrap px-4" aria-hidden="true">
                    {[...SPONSORS, ...SPONSORS].map((s, i) => (
                        <div key={`top-2-${i}`} className="flex items-center gap-2">
                            <span className={`text-[9px] font-black italic tracking-tighter ${s.color}`}>{s.name}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Left Ad Boards */}
            <div className="absolute top-0 left-0 bottom-0 w-4 bg-black/60 flex flex-col items-center overflow-hidden border-r border-white/5 z-10">
                <div className="flex flex-col items-center gap-12 animate-marquee-vertical py-4 whitespace-nowrap h-fit">
                    {[...SPONSORS, ...SPONSORS].map((s, i) => (
                        <span key={`left-1-${i}`} className={`text-[8px] font-black italic tracking-tighter rotate-90 ${s.color} transition-all`}>{s.name}</span>
                    ))}
                    {[...SPONSORS, ...SPONSORS].map((s, i) => (
                        <span key={`left-2-${i}`} className={`text-[8px] font-black italic tracking-tighter rotate-90 ${s.color} transition-all`} aria-hidden="true">{s.name}</span>
                    ))}
                </div>
            </div>

            {/* Right Ad Boards */}
            <div className="absolute top-0 right-0 bottom-0 w-4 bg-black/60 flex flex-col items-center overflow-hidden border-l border-white/5 z-10">
                <div className="flex flex-col items-center gap-12 animate-marquee-vertical-reverse py-4 whitespace-nowrap h-fit">
                    {[...SPONSORS, ...SPONSORS].map((s, i) => (
                        <span key={`right-1-${i}`} className={`text-[8px] font-black italic tracking-tighter -rotate-90 ${s.color} transition-all`}>{s.name}</span>
                    ))}
                    {[...SPONSORS, ...SPONSORS].map((s, i) => (
                        <span key={`right-2-${i}`} className={`text-[8px] font-black italic tracking-tighter -rotate-90 ${s.color} transition-all`} aria-hidden="true">{s.name}</span>
                    ))}
                </div>
            </div>

            {/* Bottom Ad Boards */}
            <div className="absolute bottom-0 left-0 right-0 h-6 bg-black/80 flex items-center overflow-hidden border-t border-white/10 z-10">
                <div className="flex items-center gap-12 animate-marquee-reverse whitespace-nowrap px-4">
                    {[...SPONSORS, ...SPONSORS].map((s, i) => (
                        <div key={`bottom-1-${i}`} className="flex items-center gap-2">
                            <span className={`text-[9px] font-black italic tracking-tighter ${s.color}`}>{s.name}</span>
                        </div>
                    ))}
                </div>
                <div className="flex items-center gap-12 animate-marquee-reverse whitespace-nowrap px-4" aria-hidden="true">
                    {[...SPONSORS, ...SPONSORS].map((s, i) => (
                        <div key={`bottom-2-${i}`} className="flex items-center gap-2">
                            <span className={`text-[9px] font-black italic tracking-tighter ${s.color}`}>{s.name}</span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="absolute bottom-6 right-6 w-20 h-20 md:w-24 md:h-24 opacity-10 pointer-events-none z-0 grayscale-[0.5]">
                <img src={currentTeamLogo} alt="" className="w-full h-full object-contain" />
            </div>
          </div>
        </div>

        <div 
            className="relative aspect-[4/5] md:aspect-[3/4] w-full z-10 px-[8%] py-[10%] transition-transform duration-700 ease-out pointer-events-none"
            style={{
                transform: `rotateY(${rotation.y * 0.5}deg) translateY(${rotation.y * 0.1}%)`
            }}
        >
          {FORMATIONS[formation].map((pos, idx) => {
            const assignedPlayer = lineup[idx];
            const isTargeted = isMovingPlayer !== null && isMovingPlayer !== idx;
            const isCurrentlyDragOver = dragOverIndex === idx;
            const isBeingDragged = draggedIndex === idx;
            
            return (
              <div 
                key={idx}
                className={`absolute flex flex-col items-center -translate-x-1/2 -translate-y-1/2 transition-all duration-700 ease-[0.32,0.72,0,1] ${activeSlotMenu === idx ? 'z-50' : 'z-10'} ${isBeingDragged ? 'opacity-40' : 'opacity-100'} pointer-events-auto`}
                style={{ 
                  left: `${pos.x}%`, 
                  top: `${pos.y}%`,
                  transform: 'translateX(-50%) translateY(-50%)',
                }}
                onDragOver={(e) => onDragOver(e, idx)}
                onDrop={(e) => onDrop(e, idx)}
              >
                <div 
                  draggable={!!assignedPlayer}
                  onDragStart={(e) => onDragStart(e, idx)}
                  onDragEnd={onDragEnd}
                  className={`relative w-14 h-14 md:w-18 md:h-18 flex items-center justify-center transition-all duration-300 cursor-pointer ${
                    assignedPlayer 
                    ? `${isCurrentlyDragOver ? 'ring-4 ring-emerald-500 scale-110' : isMovingPlayer === idx ? 'ring-4 ring-rose-500 scale-110 shadow-rose-500/20' : 'md:hover:scale-110'}` 
                    : `rounded-2xl ${currentTheme === 'Paper' ? 'bg-slate-900/5' : 'bg-black/20 backdrop-blur-md'} border-2 border-dashed ${isCurrentlyDragOver || isTargeted ? 'border-emerald-500 bg-emerald-500/20 animate-pulse' : currentTheme === 'Paper' ? 'border-slate-900/10' : 'border-white/10 md:hover:border-rose-400/50 md:hover:bg-white/10'}`
                  }`}
                  style={{
                    filter: 'drop-shadow(0 20px 30px rgba(0,0,0,0.4))'
                  }}
                  onClick={() => handleSlotClick(idx)}
                >
                  {assignedPlayer ? (
                    <div className="relative w-full h-full flex items-center justify-center">
                      <div className="absolute inset-[12%] overflow-hidden rounded-lg z-0">
                        <img 
                          src={assignedPlayer.player.photo} 
                          className={`w-full h-full object-cover animate-in zoom-in duration-300 pointer-events-none ${currentTheme === 'Paper' ? 'grayscale opacity-90' : ''}`} 
                          alt="" 
                        />
                      </div>
                      {assignedPlayer.frame && assignedPlayer.frame !== 'none' ? (
                        <div className="absolute inset-0 pointer-events-none z-10 scale-[1.12]">
                          <img 
                            src={PLAYER_FRAMES.find(f => f.id === assignedPlayer.frame)?.image} 
                            className="w-full h-full object-contain animate-in zoom-in duration-500" 
                            alt="" 
                          />
                        </div>
                      ) : (
                        <div className="absolute inset-0 border-2 border-white/20 rounded-2xl shadow-sm z-10" />
                      )}
                    </div>
                  ) : (
                    <Plus size={24} className={`${isCurrentlyDragOver || isTargeted ? 'text-emerald-400' : currentTheme === 'Paper' ? 'text-slate-900/10' : 'text-white/10'}`} />
                  )}

                  <div className={`absolute -bottom-7 left-1/2 -translate-x-1/2 text-white text-[7px] md:text-[8px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full border-white/10 transition-colors z-20 min-w-[75px] max-w-[130px] whitespace-nowrap overflow-hidden text-ellipsis text-center text-shadow-md ${
                    isMovingPlayer === idx ? 'bg-rose-500 border-rose-400' : (isCurrentlyDragOver || isTargeted) ? 'bg-emerald-600 border-emerald-400' : currentTheme === 'Paper' ? 'text-slate-800' : 'backdrop-blur-sm'
                  }`}>
                    {assignedPlayer ? assignedPlayer.player.lastname || assignedPlayer.player.name : pos.pos}
                  </div>

                  {activeSlotMenu === idx && (
                    <div 
                      ref={slotMenuRef} 
                      className="absolute top-full left-1/2 -translate-x-1/2 mt-5 w-40 bg-white/95 backdrop-blur-xl rounded-2xl shadow-md border border-gray-100 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 z-[100] cursor-default" 
                      onClick={e => e.stopPropagation()}
                    >
                        <div className="p-1.5 flex flex-col gap-0.5">
                            <button 
                              onClick={() => { openPlayerSelect(idx); setActiveSlotMenu(null); }} 
                              className="flex items-center gap-2.5 px-2 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest text-slate-700 md:hover:bg-slate-100 transition-colors"
                            >
                                <RefreshCcw size={12} className="text-indigo-500" /> 
                                <span>{t(lang, 'edit')}</span>
                            </button>
                            <button 
                              onClick={() => { setIsMovingPlayer(idx); setActiveSlotMenu(null); }} 
                              className="flex items-center gap-2.5 px-2 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest text-slate-700 md:hover:bg-slate-100 transition-colors"
                            >
                                <ArrowRightLeft size={12} className="text-emerald-500" /> 
                                <span>{t(lang, 'move')}</span>
                            </button>
                            {/* <button 
                              onClick={() => { setSelectingIndex(idx); setIsFrameModalOpen(true); setActiveSlotMenu(null); }} 
                              className="flex items-center gap-2.5 px-2 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest text-slate-700 md:hover:bg-slate-100 transition-colors"
                            >
                                <Grid3X3 size={12} className="text-amber-500" /> 
                                <span>{t(lang, 'frame')}</span>
                            </button> */}
                            <button 
                              onClick={() => clearSlot(idx)} 
                              className="flex items-center gap-2.5 px-2 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest text-rose-500 md:hover:bg-rose-50 transition-colors"
                            >
                                <Trash2 size={12} /> 
                                <span>{t(lang, 'clear')}</span>
                            </button>
                        </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
        {(isMovingPlayer !== null || draggedIndex !== null) && (
          <div className="absolute top-10 left-1/2 -translate-x-1/2 z-40 bg-rose-500 text-white px-6 py-3 rounded-full font-black text-[10px] uppercase tracking-widest shadow-md animate-bounce border-2 border-white/20">
            {guide.swapTip}
          </div>
        )}
      </div>

      {/* Control Buttons */}
      <div className="flex justify-center gap-4 mt-6">
          <button 
            onClick={() => handleCapture('download')}
            disabled={isCapturing}
            className="flex items-center gap-2.5 bg-white border border-gray-200 px-6 py-3 rounded-2xl font-black text-[11px] uppercase tracking-widest text-slate-700 md:hover:bg-slate-50 active:scale-95 shadow-md shadow-slate-100/50 transition-all disabled:opacity-50"
          >
            {isCapturing ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} className="text-rose-500" />}
            {guide.save}
          </button>
          <button 
            onClick={() => handleCapture('share')}
            disabled={isCapturing}
            className="flex items-center gap-2.5 bg-slate-900 border border-slate-800 px-6 py-3 rounded-2xl font-black text-[11px] uppercase tracking-widest text-white active:scale-95 shadow-md shadow-rose-100 transition-all disabled:opacity-50"
          >
            {isCapturing ? <Loader2 size={16} className="animate-spin" /> : <Share2 size={16} className="text-rose-400" />}
            {guide.share}
          </button>
      </div>

      {/* Player Selection Modal */}
      {isPlayerModalOpen && (
        <div className={`fixed inset-0 z-[300] flex flex-col justify-end md:items-center md:justify-center transition-opacity duration-500 !mt-0 ${isPlayerAnimating ? 'opacity-100' : 'opacity-0'}`}>
            <div className="absolute inset-0 bg-black/60 backdrop-blur-[4px]" onClick={handleClosePlayerModal}></div>
            <div className={`relative bg-white w-full h-[85vh] md:max-w-xl md:rounded-[40px] rounded-t-[40px] shadow-md flex flex-col overflow-hidden transition-transform duration-700 ease-[0.32,0.72,0,1] ${isPlayerAnimating ? 'translate-y-0' : 'translate-y-full'}`}>
                <header className="p-6 md:p-8 flex items-center justify-between border-b border-gray-100 shrink-0">
                    <div>
                        <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight leading-none">{currentTeamName}</h3>
                        <p className="text-[10px] font-black text-rose-500 uppercase tracking-widest mt-1">{t(lang, 'selectPersonnel')}</p>
                    </div>
                    <button onClick={handleClosePlayerModal} className="p-2.5 bg-gray-50 text-slate-400 md:hover:text-slate-900 rounded-full transition-all"><X size={20} strokeWidth={3} /></button>
                </header>

                <div className="px-6 md:px-8 py-4 bg-white/95 backdrop-blur-md sticky top-0 z-20 overflow-x-auto no-scrollbar flex gap-2 shrink-0">
                    {['All', 'Forwards', 'Midfielders', 'Defenders', 'Goalkeepers'].map(filter => (
                        <button 
                            key={filter} 
                            onClick={() => setActiveFilter(filter as any)}
                            className={`whitespace-nowrap px-5 py-2.5 rounded-full text-[11px] font-black uppercase tracking-widest transition-all flex items-center gap-2 ${
                                activeFilter === filter ? 'bg-slate-900 text-white shadow-md scale-105' : 'bg-gray-50 text-slate-400 md:hover:bg-gray-100'
                            }`}
                        >
                            {t(lang, filter.toLowerCase()) || filter}
                            <span className={`text-[9px] px-1.5 rounded-md ${activeFilter === filter ? 'bg-white/20' : 'bg-slate-200 text-slate-500'}`}>
                                {playerCounts[filter as keyof typeof playerCounts]}
                            </span>
                        </button>
                    ))}
                </div>

                <div className="flex-1 overflow-y-auto p-6 md:p-8 custom-scrollbar space-y-4">
                    {loadingPlayers ? (
                        <div className="h-full flex flex-col items-center justify-center gap-4 py-20">
                            <Loader2 className="animate-spin text-indigo-500" size={32} />
                            <span className="text-[10px] font-black text-slate-300 uppercase tracking-[0.2em]">{t(lang as Language, 'searchingSector')}</span>
                        </div>
                    ) : filteredPlayers.length > 0 ? (
                        <div className="flex flex-col gap-4">
                            {filteredPlayers.map((p: any) => {
                                const isAlreadyInLineup = selectedPlayerIds.includes(p.player.id);
                                return (
                                    <button 
                                        key={p.player.id} 
                                        onClick={() => !isAlreadyInLineup && selectPlayer(p)}
                                        disabled={isAlreadyInLineup}
                                        className={`flex items-center gap-4 p-4 bg-white border rounded-[28px] transition-all text-left group relative ${
                                          isAlreadyInLineup ? 'opacity-50 grayscale bg-slate-50 border-gray-100' : 'md:hover:border-indigo-500 md:hover:shadow-md border-gray-100 active:scale-[0.98]'
                                        }`}
                                    >
                                        <div className="w-16 h-16 rounded-2xl overflow-hidden bg-slate-100 border-2 border-white shadow-md shrink-0 relative">
                                            <img src={p.player.photo} className="w-full h-full object-cover" alt="" />
                                            {isAlreadyInLineup && <div className="absolute inset-0 bg-slate-900/40 flex items-center justify-center"><ShieldCheck size={24} className="text-white" /></div>}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h4 className={`font-black uppercase tracking-tight truncate leading-none ${isAlreadyInLineup ? 'text-slate-400' : 'text-slate-900'}`}>{p.player.name}</h4>
                                            <div className="flex items-center gap-2 mt-1.5"><div className="w-4 h-4"><img src={currentTeamLogo} className="w-full h-full object-contain" alt="" /></div><span className="text-[9px] font-bold text-slate-300 uppercase tracking-widest truncate">{currentTeamName}</span></div>
                                        </div>
                                        {!isAlreadyInLineup && <div className="text-indigo-500 opacity-0 md:group-hover:opacity-100 transition-opacity pr-2"><Check size={20} strokeWidth={3} /></div>}
                                    </button>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="py-20 text-center flex flex-col items-center gap-4"><User size={48} className="text-slate-100" /><p className="text-xs font-black text-slate-300 uppercase tracking-widest">{t(lang, 'personnelFound')}</p></div>
                    )}
                </div>
            </div>
        </div>
      )}

      {/* Formation Modal */}
      {isFormationModalOpen && (
        <div className={`fixed inset-0 z-[300] flex flex-col justify-end md:items-center md:justify-center transition-opacity duration-500 !mt-0 ${isFormationAnimating ? 'opacity-100' : 'opacity-0'}`}>
            <div className="absolute inset-0 bg-black/60 backdrop-blur-[4px]" onClick={handleCloseFormationModal}></div>
            <div className={`relative bg-white w-full h-[60vh] md:h-auto md:max-w-lg md:rounded-[40px] rounded-t-[40px] shadow-md flex flex-col overflow-hidden transition-transform duration-700 ease-[0.32,0.72,0,1] ${isFormationAnimating ? 'translate-y-0' : 'translate-y-full'}`}>
                <header className="p-6 flex items-center justify-between border-b border-gray-100 shrink-0">
                    <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight leading-none">{t(lang as Language, 'selectFormation')}</h3>
                    <button onClick={handleCloseFormationModal} className="p-2 bg-gray-50 text-slate-400 md:hover:text-slate-900 rounded-full transition-all"><X size={18} strokeWidth={3} /></button>
                </header>
                <div className="p-4 space-y-2">
                    {(Object.keys(FORMATIONS) as Formation[]).map(f => (
                        <button 
                            key={f} 
                            onClick={() => { setFormation(f); handleCloseFormationModal(); }} 
                            className={`w-full flex items-center justify-between px-6 py-5 rounded-[22px] transition-all border ${
                                formation === f ? 'bg-slate-900 text-white border-slate-900 shadow-md' : 'bg-white text-slate-600 border-gray-100 md:hover:border-rose-200'
                            }`}
                        >
                            <span className="text-sm font-black uppercase tracking-[0.2em]">{f}</span>
                            {formation === f && <Check size={18} strokeWidth={4} className="text-rose-500" />}
                        </button>
                    ))}
                </div>
                <div className="h-6 shrink-0 md:hidden bg-white"></div>
            </div>
        </div>
      )}

      {/* Theme Modal */}
      {isThemeModalOpen && (
        <div className={`fixed inset-0 z-[300] flex flex-col justify-end md:items-center md:justify-center transition-opacity duration-500 !mt-0 ${isThemeAnimating ? 'opacity-100' : 'opacity-0'}`}>
            <div className="absolute inset-0 bg-black/60 backdrop-blur-[4px]" onClick={handleCloseThemeModal}></div>
            <div className={`relative bg-white w-full h-[70vh] md:h-auto md:max-w-lg md:rounded-[40px] rounded-t-[40px] shadow-md flex flex-col overflow-hidden transition-transform duration-700 ease-[0.32,0.72,0,1] ${isThemeAnimating ? 'translate-y-0' : 'translate-y-full'}`}>
                <header className="p-6 flex items-center justify-between border-b border-gray-100 shrink-0">
                    <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight leading-none">{t(lang, 'squadTheme')}</h3>
                    <button onClick={handleCloseThemeModal} className="p-2 bg-gray-50 text-slate-400 md:hover:text-slate-900 rounded-full transition-all"><X size={18} strokeWidth={3} /></button>
                </header>
                <div className="p-4 grid grid-cols-3 gap-2 overflow-y-auto">
                    {themeOptions.map(opt => (
                        <button 
                            key={opt.id} 
                            onClick={() => { setCurrentTheme(opt.id); handleCloseThemeModal(); }} 
                            className={`flex flex-col items-center gap-2 p-3 py-4 rounded-[22px] transition-all border-2 ${
                                currentTheme === opt.id ? 'border-rose-500 bg-rose-50/30' : 'border-gray-100 md:hover:border-slate-200'
                            }`}
                        >
                            <div className={`w-10 h-10 rounded-xl shadow-md border border-white/20 ${opt.iconBg}`} />
                            <span className={`text-[8px] font-black uppercase tracking-tighter text-center leading-tight h-4 flex items-center ${currentTheme === opt.id ? 'text-rose-600' : 'text-slate-500'}`}>{opt.label}</span>
                        </button>
                    ))}
                </div>
                <div className="h-6 shrink-0 md:hidden bg-white"></div>
            </div>
        </div>
      )}

      {/* Frame Selection Modal */}
      {isFrameModalOpen && (
        <div className={`fixed inset-0 z-[300] flex flex-col justify-end md:items-center md:justify-center transition-opacity duration-500 !mt-0 ${isFrameAnimating ? 'opacity-100' : 'opacity-0'}`}>
            <div className="absolute inset-0 bg-black/60 backdrop-blur-[4px]" onClick={handleCloseFrameModal}></div>
            <div className={`relative bg-white w-full h-[50vh] md:h-auto md:max-w-lg md:rounded-[40px] rounded-t-[40px] shadow-md flex flex-col overflow-hidden transition-transform duration-700 ease-[0.32,0.72,0,1] ${isFrameAnimating ? 'translate-y-0' : 'translate-y-full'}`}>
                <header className="p-6 flex items-center justify-between border-b border-gray-100 shrink-0">
                    <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight leading-none">{t(lang, 'selectFrame')}</h3>
                    <button onClick={handleCloseFrameModal} className="p-2 bg-gray-50 text-slate-400 md:hover:text-slate-900 rounded-full transition-all"><X size={18} strokeWidth={3} /></button>
                </header>
                <div className="p-6 grid grid-cols-4 gap-4 overflow-y-auto">
                    {PLAYER_FRAMES.map(frame => (
                        <button 
                            key={frame.id} 
                            onClick={() => selectFrame(frame.id)} 
                            className={`flex flex-col items-center gap-3 p-2 rounded-2xl transition-all border-2 ${
                                (lineup[selectingIndex!]?.frame || 'none') === frame.id ? 'border-rose-500 bg-rose-50/30' : 'border-gray-100 md:hover:border-slate-200'
                            }`}
                        >
                            <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center relative overflow-hidden border border-gray-100">
                                {frame.id === 'none' ? (
                                    <X size={20} className="text-slate-300" />
                                ) : (
                                    <img src={frame.image} className="w-full h-full object-contain" alt="" />
                                )}
                            </div>
                            <span className="text-[9px] font-black uppercase tracking-tighter text-slate-500">{t(lang, frame.label)}</span>
                        </button>
                    ))}
                </div>
                <div className="h-6 shrink-0 md:hidden bg-white"></div>
            </div>
        </div>
      )}
    </div>
  );
};
